<!--
  BEWARE: This page has many similarities with the PageDetail.vue and the AnimalPageDetail.vue template,
  changes for this template may affect the PageDetail.vue and the AnimalPageDetail.vue template as well.
-->

<template>
  <TemplateBase>
    <main class="relative z-0 col-span-full row-start-2">
      <!-- Breadcrumbs -->
      <OrganismNavigationBreadcrumb :path="breadcrumbs" class="container mb-4 mx-auto pt-10 px-4 md:mb-8" />

      <!-- Section with container and grid for page content -->
      <section
        id="article-detail-page-content"
        class="container relative grid grid-cols-12 mx-auto px-4"
        :class="{
          'page-image-container': page.image,
          'margin-bottom-page-content-with-content-items-following': page.matrix.length,
          'margin-bottom-page-content-without-content-items-following': !page.matrix.length && !page.references.length,
        }"
      >
        <!-- Div with responsive page layout in columns -->
        <div v-if="page.image || page.description" class="relative z-40 col-span-full 2xl:col-end-10 2xl:col-start-4 lg:col-end-12 lg:col-start-2 xl:col-end-11 xl:col-start-3">
          <!-- White sheet with page content -->
          <div
            class="padding-page-content relative flex flex-col bg-white rounded-t-xl"
            :class="{ 'rounded-b-xl': page.matrix.length || !page.references.length, 'rounded-b-none': !page.matrix.length }"
          >
            <time class="block mb-1 whitespace-nowrap text-sm leading-none uppercase md:mb-3 md:text-lg">
              {{ $d(new Date(page.date), 'medium-date') }}
            </time>

            <!-- Page header -->
            <h1 class="mb-1 text-2xl text-black font-black leading-tight uppercase md:mb-4 md:text-3xl md:font-bold md:leading-none lg:text-4xl xl:text-5xl">
              {{ page.title }}
            </h1>

            <h2 v-if="page.subtitle" class="mb-4 text-xl font-medium leading-tight md:mb-8 md:font-bold lg:text-2xl">
              {{ page.subtitle }}
            </h2>

            <!-- Page introduction -->
            <div v-if="page.introduction" class="prose prose--app order-2 mb-6 max-w-none font-bold overflow-auto md:order-1 md:mb-10 lg:text-lg" v-html="page.introduction" />

            <MoleculeWriter v-if="page.writer" :title="page.writer.title" :role="page.writer.role" :image="page.writer.image" />

            <!-- youtube video -->
            <section
              v-if="get(page, 'youtube.code')"
              class="page-image margin-bottom-page-content-without-content-items-following relative order-1 rounded-xl overflow-hidden md:order-2"
            >
              <div class="aspect-video youtube rounded-xl overflow-hidden">
                <div class="w-full h-full" v-html="get(page, 'youtube.code')" />
              </div>
            </section>

            <MoleculePicture
              v-else-if="page.image"
              :image="page.image"
              :alt="page.title"
              class="page-image margin-bottom-page-content-without-content-items-following relative order-1 rounded-xl overflow-hidden md:order-2 lg:-mx-32"
            />

            <!-- Page body text -->
            <div v-if="page.description" class="prose prose--app order-2 max-w-none overflow-auto" v-html="page.description" />
          </div>
        </div>
      </section>

      <!-- Possible content items -->
      <OrganismMatrix
        v-if="page.matrix.length"
        :items="page.matrix"
        :enable-last-item-rounded="page.references.length > 0"
        :expiry-date="page.expiryDate || null"
        :class="{ 'margin-bottom-page-content-without-content-items-following': !page.references.length }"
      />

      <!-- Possible references to other pages -->
      <MoleculePageDetailReferences class="margin-bottom-page-content-without-content-items-following" :references="[...page.references, ...page.overridableReferences]" />

      <section v-if="page.donationReference && page.donationReference.url" id="article-detail-page-donation" class="container relative z-10 mb-16 mx-auto 2xl:px-56 px-6 sm:px-8">
        <div class="relative">
          <MoleculePicture :image="page.donationReference.image" class="cover-fill hidden w-full h-full lg:block" />
          <div class="bottom-0 right-0 w-full lg:absolute lg:max-w-xl">
            <MoleculeDonation
              :title="page.donationReference.title"
              :subtitle="page.donationReference.subtitle"
              :introduction="page.donationReference.introduction"
              :image="page.donationReference.image"
              :url="page.donationReference.url"
              :price1="page.donationReference.donationPrice1"
              :price2="page.donationReference.donationPrice2"
              :price3="page.donationReference.donationPrice3"
            />
          </div>
        </div>
      </section>
    </main>
  </TemplateBase>
</template>

<script setup lang="ts">
import get from 'lodash/get'

interface Breadcrumb {
  name: string // Define others fields as needed
}

interface Writer {
  title: string
  role: string
  image: string
}

interface Reference {
  url: string
  image: string
  title: string
  subtitle: string
  introduction: string
  donationPrice1: number
  donationPrice2: number
  donationPrice3: number
}

interface Page {
  image: string | null
  description: string | null
  date: string
  title: string
  subtitle: string | null
  introduction: string | null
  writer: Writer | null
  matrix: Array<any>
  references: Array<any>
  overridableReferences: Array<any>
  youtube: { code: string | null }
  donationReference: Reference | null
}

defineProps<{
  breadcrumbs: Breadcrumb[]
  page: Page
}>()
</script>
